import React, { useState, useEffect, useRef, useContext } from "react";
import AppLayout from "../component/AppLayout";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../component/UserContext";
import { Zap, X, CheckCircle2, BoltIcon, Layout, Sparkles } from "lucide-react";
import { FiZap } from "react-icons/fi";

export default function CustomTemplate() {
  const location = useLocation();
  const { user, setUser: setContextUser } = useContext(UserContext);

  const plan = user?.subscription?.plan || "free";
  const isPro = plan === "pro";

  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiActive, setAiActive] = useState(user?.Ai || false);
  const [togglingAi, setTogglingAi] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    if (user?.Ai !== undefined) {
      setAiEnabled(user.Ai);
      setAiActive(user.Ai);
    }
  }, [user]);

  const aiBlockToast = () =>
    toast.error(
      "Auto response is enabled. Switch to manual mode to edit templates.",
    );

  const verifyCompanyProfile = async (targetUserId) => {
    if (!targetUserId) return false;
    const urls = [
      `https://email-syncing-backend.onrender.com/api/company-profile/${targetUserId}`,
      `https://email-syncing-backend.onrender.com/api/company-profile/${targetUserId}`,
      `https://email-syncing-backend.onrender.com/auth/user-profile/${targetUserId}`,
      `https://email-syncing-backend.onrender.com/auth/user-profile/${targetUserId}`,
    ];
    for (const url of urls) {
      try {
        const res = await axios.get(url);
        const data = res.data?.data || res.data?.company || res.data?.user || res.data;
        const cp = data?.company || data;
        const name = cp?.companyName || cp?.name || cp?.businessName || cp?.organizationName || "";
        const desc = cp?.businessDescription || cp?.description || cp?.companyKnowledge || "";
        if (typeof name === "string" && name.trim().length > 0 && typeof desc === "string" && desc.trim().length > 0) {
          return true;
        }
      } catch (e) {
        // try next URL
      }
    }
    return false;
  };

  const handleToggleAiReplies = async () => {
    const targetUserId = localStorage.getItem("userid") || user?._id;
    if (!targetUserId) return;
    const nextStatus = !aiActive;

    if (nextStatus) {
      const isComplete = await verifyCompanyProfile(targetUserId);
      if (!isComplete) {
        toast.error("Please complete your Company Profile (Business Name & Description) first before turning on AI replies.", { duration: 4000 });
        navigate("/company-profile");
        return;
      }
    }

    setAiActive(nextStatus);
    setTemplates((prev) => prev.map((t) => ({ ...t, aiResponse: nextStatus })));
    try {
      setTogglingAi(true);
      const res = await axios.post(
        `https://email-syncing-backend.onrender.com/auth/toggle-ai-replies/${targetUserId}`,
        { enabled: nextStatus, userId: targetUserId }
      );
      await axios.patch("https://email-syncing-backend.onrender.com/template/ai-toggle-all", {
        userId: targetUserId,
        platform: "other",
        aiResponse: nextStatus,
      });
      if (res.data?.user && setContextUser) setContextUser(res.data.user);
      toast.success(nextStatus ? "AI replies turned on for all custom templates." : "AI replies turned off — templates send as written.");
    } catch (err) {
      setAiActive(!nextStatus);
      fetchTemplates();
      toast.error("Could not update the reply mode.");
    } finally {
      setTogglingAi(false);
    }
  };

  const [templates, setTemplates] = useState([]);
  const urlParams = new URLSearchParams(location.search);

  const viewType = urlParams.get("view");
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [platform, setPlatform] = useState("");

  /* What the template is called, so several can be told apart. */
  const [templateName, setTemplateName] = useState("");
  const [service, setService] = useState("");
  const [content, setContent] = useState("");
  const [sequenceType, setSequenceType] = useState("");
  const [conditions, setConditions] = useState([
    { field: "subject", operator: "contains", value: "" },
  ]);
  const [globalActive, setGlobalActive] = useState(false);
  const quillRef = useRef(null);

  const navigate = useNavigate();

  const [selectedServiceFilter, setSelectedServiceFilter] = useState(
    urlParams.get("service") || "All",
  );

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem("userid") || user?._id;
      const token = localStorage.getItem("usertoken");
      const res = await axios.get(
        "https://email-syncing-backend.onrender.com/template/all/custom",
        {
          params: { userId },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      const rawTemplates = Array.isArray(res.data)
        ? res.data
        : res.data?.data || [];

      // Ensure default aiResponse is false when not explicitly set
      const normalized = rawTemplates.map((t) => ({
        ...t,
        aiResponse: t.aiResponse === true,
      }));

      setTemplates(normalized);
      setFilteredTemplates(normalized);
      const hasActive = normalized.some((t) => t.active === true);
      setGlobalActive(hasActive);
    } catch (err) {
      console.error("Error fetching templates:", err);
      toast.error("Failed to fetch templates");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTemplateAi = async (templateId, currentStatus) => {
    const nextStatus = !currentStatus;
    const targetUserId = localStorage.getItem("userid") || user?._id;

    if (nextStatus) {
      const isComplete = await verifyCompanyProfile(targetUserId);
      if (!isComplete) {
        toast.error("Please complete your Company Profile (Business Name & Description) first before turning on AI replies.", { duration: 4000 });
        navigate("/company-profile");
        return;
      }
    }

    setTemplates((prev) =>
      prev.map((t) => (t._id === templateId ? { ...t, aiResponse: nextStatus } : t))
    );
    try {
      await axios.patch(`https://email-syncing-backend.onrender.com/template/ai-toggle/${templateId}`, {
        aiResponse: nextStatus,
      });
      toast.success(nextStatus ? "This template will now be written by AI." : "This template will now send as written.");
    } catch (err) {
      setTemplates((prev) =>
        prev.map((t) => (t._id === templateId ? { ...t, aiResponse: currentStatus } : t))
      );
      toast.error("Could not update the reply mode for this template.");
    }
  };

  const handleBulkToggleAi = async (enableAll) => {
    const userId = localStorage.getItem("userid") || user?._id;
    if (!userId) return;

    if (enableAll) {
      const isComplete = await verifyCompanyProfile(userId);
      if (!isComplete) {
        toast.error("Please complete your Company Profile (Business Name & Description) first before turning on AI replies.", { duration: 4000 });
        navigate("/company-profile");
        return;
      }
    }

    setTemplates((prev) => prev.map((t) => ({ ...t, aiResponse: enableAll })));
    try {
      await axios.patch("https://email-syncing-backend.onrender.com/template/ai-toggle-all", {
        userId,
        platform: "other",
        aiResponse: enableAll,
      });
      toast.success(enableAll ? "AI replies turned on for all custom templates." : "AI replies turned off for all templates.");
    } catch (err) {
      fetchTemplates();
      toast.error("Could not update the reply mode for these templates.");
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const [selectedSequenceFilter, setSelectedSequenceFilter] = useState("All");

  const groupedTemplates = filteredTemplates.reduce((acc, tpl) => {
    const groupKey = tpl.service || "General Templates";
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(tpl);
    return acc;
  }, {});

  const insertField = (placeholder) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    editor.focus();

    const range = editor.getSelection(true);

    const index = range
      ? range.index
      : Math.max(editor.getLength() - 1, 0);

    editor.insertText(index, placeholder, "user");
    editor.setSelection(index + placeholder.length, 0, "user");
  };

  /*
   * Opens the drawer in create mode.
   *
   * The drawer already handled creating — editingId null posts to
   * /template/create — but nothing ever opened it that way, so custom
   * templates could only be edited, never made.
   */
  const handleCreateNew = () => {
    setEditingId(null);
    setTemplateName("");
    /*
     * This page lists templates with platform "other"; a blank platform
     * fails the model's required enum, so a created template would 500 and
     * never appear here anyway.
     */
    setPlatform("other");
    setService("");
    setConditions([{ field: "subject", operator: "contains", value: "" }]);
    setContent("");
    setIsDrawerOpen(true);
  };

  const handleEdit = (template) => {
    setEditingId(template._id);
    setTemplateName(template.name || "");
    setPlatform(template.platform);
    setService(template.service || "");
    setConditions(
      template.conditions?.length > 0
        ? template.conditions
        : [{ field: "subject", operator: "contains", value: "" }],
    );
    setContent(template.content);
    setIsDrawerOpen(true);
  };

  const handleSaveTemplate = async () => {
    /* The server requires a name; catching it here saves a round trip. */
    if (!templateName.trim()) {
      toast.error("Give the template a name first.");
      return;
    }

    try {
      const userId = localStorage.getItem("userid");
      const payload = {
        userId,
        name: templateName.trim(),
        /* Guard: the model requires one of shopify | other. */
        platform: platform || "other",
        service,
        conditions,
        content,
      };

      if (editingId) {
        await axios.put(
          `https://email-syncing-backend.onrender.com/template/update/${editingId}`,
          payload,
        );
        toast.success("Template updated successfully!");
      } else {
        await axios.post(
          "https://email-syncing-backend.onrender.com/template/create",
          payload,
        );
        toast.success("Template created successfully!");
      }

      setIsDrawerOpen(false);
      setEditingId(null);
      setTemplateName("");
      setPlatform("");
      setService("");
      setConditions([{ field: "subject", operator: "contains", value: "" }]);
      setContent("");
      fetchTemplates();
    } catch (err) {
      toast.error("Failed to save template");
    }
  };

  const handleToggle = async (id, currentStatus) => {
    setTemplates((prev) =>
      prev.map((tpl) =>
        tpl._id === id ? { ...tpl, active: !currentStatus } : tpl,
      ),
    );

    try {
      await axios.put(
        `https://email-syncing-backend.onrender.com/template/update/${id}`,
        {
          active: !currentStatus,
        },
      );
      toast.success(
        `Template ${!currentStatus ? "activated" : "deactivated"} successfully`,
      );
      fetchTemplates();
    } catch (err) {
      setTemplates((prev) =>
        prev.map((tpl) =>
          tpl._id === id ? { ...tpl, active: currentStatus } : tpl,
        ),
      );
      toast.error("Failed to toggle template status");
    }
  };

  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const openDeleteConfirm = (id) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const handleDeleteTemplate = async () => {
    try {
      await axios.delete(
        `https://email-syncing-backend.onrender.com/template/delete/${deleteId}`,
      );

      toast.success("Template deleted!");

      setShowDeleteConfirm(false);
      setDeleteId(null);

      fetchTemplates();
    } catch (err) {
      toast.error("Failed to delete template");
    }
  };

  const handleGlobalToggle = async () => {
    try {
      const userId = localStorage.getItem("userid");
      const res = await axios.patch(
        "https://email-syncing-backend.onrender.com/template/templatestatus/all/other",
        { userId },
      );
      if (res.data.success) {
        const newStatus = res.data.toggledTo;
        setGlobalActive(newStatus);
        toast.success(
          newStatus ? "All templates activated" : "All templates deactivated",
        );
        fetchTemplates();
      } else {
        toast.error(res.data.message || "Failed to update all templates");
      }
    } catch (err) {
      toast.error("Error updating all templates");
    }
  };

  useEffect(() => {
    let filtered = [...templates];

    if (selectedSequenceFilter !== "All") {
      filtered = filtered.filter((t) => t.name === selectedSequenceFilter);
    }

    setFilteredTemplates(filtered);
  }, [templates, selectedSequenceFilter]);

  const Loader = () => (
    <div className="flex flex-col justify-center items-center py-12">
      <svg
        className="animate-spin h-7 w-7 text-slate-900 mb-2"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        ></circle>
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
      <p className="text-xs text-slate-600 font-semibold">
        Loading templates...
      </p>
    </div>
  );

  return (
    <AppLayout>
      <div className="w-full flex-1 min-w-0 h-full overflow-y-auto bg-[#FAF8F5]">
        {/* Header Section */}
         <div className="border-b border-gray-200 bg-white">
        <div className="flex min-h-[30px] items-center justify-between gap-4 px-6 text-[11px] text-gray-500">
          <div className="flex min-w-0 items-center divide-x divide-gray-200">
            <div className="flex items-center gap-1.5 pr-4 font-medium text-green-700">
              <span className="relative flex h-3 w-3 items-center justify-center">
                <span className="absolute h-3 w-3 rounded-full bg-green-200" />
                <span className="relative h-2 w-2 rounded-full bg-green-500" />
              </span>

              <span>All systems live</span>
            </div>

            <div className="hidden px-4 sm:block">
              Custom Templates
              {/* {user?.email ? ` · ${user.email}` : ""} */}
            </div>

            <div className="hidden px-4 md:block">
              {/* Filter matched {stats?.processed || 0} leads today */}
            </div>

            <div className="hidden px-4 lg:block">
              Manage and automate your custom email template responses.
            </div>
          </div>

         
        </div>
      </div>

        {/* Filter Bar & Controls */}
        <div className="border-b border-gray-200 bg-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-6 py-3">
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-slate-700 whitespace-nowrap">
                Filter by Template:
              </label>

              <select
                value={selectedSequenceFilter}
                onChange={(e) => setSelectedSequenceFilter(e.target.value)}
                className="border border-slate-300 rounded-[8px] px-3 py-1.5 text-xs font-semibold text-slate-800 bg-white focus:border-slate-800 outline-none shadow-2xs w-48 sm:w-56 transition"
              >
                <option value="All">All Templates</option>
                {[...new Set(templates.map((t) => t.name).filter(Boolean))].map(
                  (name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  )
                )}
              </select>
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleCreateNew}
                className="flex items-center gap-1.5 rounded-[10px] bg-[#111110] px-3.5 py-1.5 text-xs font-bold text-white shadow-2xs transition hover:bg-black cursor-pointer"
              >
                <span className="text-sm leading-none">+</span>
                <span>New template</span>
              </button>

              {/* AI Replies Toggle */}
              <button
                type="button"
                onClick={handleToggleAiReplies}
                disabled={togglingAi}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-[10px] border text-xs font-bold transition cursor-pointer shadow-2xs ${
                  aiActive
                    ? "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100"
                    : "bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200"
                }`}
                title={aiActive ? "AI Replies are ACTIVE — click to pause" : "AI Replies are PAUSED — click to activate"}
              >
                <span className={`h-2 w-2 rounded-full ${aiActive ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
                <FiZap size={13} className={aiActive ? "text-emerald-700" : "text-slate-500"} />
                <span>{aiActive ? "AI Replies: Active" : "AI Replies: Paused"}</span>
              </button>

              <div className="flex items-center gap-3 px-3.5 py-1.5 bg-slate-50 rounded-[10px] border border-slate-200">
                <div className="flex items-center gap-1.5">
                  <Zap
                    size={15}
                    className={globalActive ? "text-emerald-600 fill-emerald-100" : "text-slate-400"}
                  />
                  <span className="text-xs font-bold text-slate-800 whitespace-nowrap">
                    Template Status
                  </span>
                </div>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={globalActive}
                    onChange={() => {
                      if (aiEnabled) return aiBlockToast();
                      handleGlobalToggle();
                    }}
                    className="sr-only peer"
                  />

                  <div
                    className={`w-9 h-5 rounded-full transition-all duration-300 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all
                      ${aiEnabled
                        ? "bg-slate-200"
                        : globalActive
                          ? "bg-emerald-600 peer-checked:after:translate-x-4"
                          : "bg-slate-300"
                      }
                    `}
                  ></div>
                </label>
              </div>
            </div>
          </div>

          {viewType && (
            <div className="mt-3 bg-emerald-50 px-4 py-2 rounded-[8px] text-xs font-medium text-emerald-900 border border-emerald-200 flex items-center justify-between">
              <span>Viewing all <strong>{viewType}</strong> templates</span>
              <button
                onClick={() => navigate("/templates")}
                className="text-emerald-700 font-bold underline hover:text-emerald-900 cursor-pointer"
              >
                Clear Filter
              </button>
            </div>
          )}
        </div>

        {/* Content Table */}
        <main className="pb-12">
          <div className="bg-white border-b border-slate-200 overflow-hidden">
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full border-collapse text-xs">
                <thead className="bg-[#111110] text-white">
                  <tr>
                    {["Label", "Template Preview", "Status", "Action"].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="text-center">
                        <Loader />
                      </td>
                    </tr>
                  ) : Object.keys(groupedTemplates).length > 0 ? (
                    Object.entries(groupedTemplates).map(([srv, group]) => (
                      <React.Fragment key={srv}>
                        <tr className="bg-slate-100/90 border-t border-b border-slate-200">
                          <td
                            colSpan="4"
                            className="px-4 py-2.5 font-bold text-slate-900 text-xs tracking-tight"
                          >
                            {srv}
                          </td>
                        </tr>

                        {group.map((t) => (
                          <tr
                            key={t._id}
                            className={`transition ${
                              aiEnabled ? "bg-slate-50/50" : "hover:bg-slate-50/80"
                            }`}
                            onClick={() => aiEnabled && aiBlockToast()}
                          >
                            <td className="px-4 py-3 text-xs font-semibold text-slate-800">
                              {t.name}
                            </td>

                            <td className="px-4 py-3 text-xs max-w-[280px]">
                              <span className="text-slate-500 text-[11px] font-normal truncate block max-w-[250px]">
                                {(t.content || "").replace(/<[^>]+>/g, "").slice(0, 65)}...
                              </span>
                            </td>

                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-2.5">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleTemplateAi(t._id, t.aiResponse !== false);
                                  }}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border transition cursor-pointer shadow-2xs ${
                                    t.aiResponse !== false
                                      ? "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100"
                                      : "bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200"
                                  }`}
                                  title="Switch this template between AI-written and sent as written"
                                >
                                  <BoltIcon className={`w-3.5 h-3.5 ${t.aiResponse !== false ? "text-emerald-600 fill-emerald-100" : "text-slate-400"}`} />
                                  <span>{t.aiResponse !== false ? "AI Reply" : "Manual Template"}</span>
                                </button>

                                <label
                                  className={`relative inline-flex items-center ${
                                    t.aiResponse !== false || aiEnabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                                  }`}
                                  title={
                                    t.aiResponse !== false
                                      ? "Status toggle is inactive while Auto Reply is enabled."
                                      : "Toggle template active status"
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    checked={t.aiResponse !== false ? false : t.active}
                                    disabled={t.aiResponse !== false || aiEnabled}
                                    onChange={() => {
                                      if (t.aiResponse !== false) {
                                        toast.error("Auto Reply is active on this template. Switch to Fixed Template mode to change status.");
                                        return;
                                      }
                                      if (aiEnabled) return aiBlockToast();
                                      handleToggle(t._id, t.active);
                                    }}
                                    className="sr-only peer"
                                  />
                                  <div
                                    className={`w-9 h-5 rounded-full transition-colors ${
                                      t.aiResponse !== false || aiEnabled
                                        ? "bg-slate-200"
                                        : "bg-slate-300 peer-checked:bg-emerald-600"
                                    }`}
                                  ></div>
                                  <div
                                    className={`absolute left-[2px] top-[2px] w-4 h-4 bg-white rounded-full shadow-xs transition-transform ${
                                      t.aiResponse === false && !aiEnabled && t.active ? "translate-x-4" : ""
                                    }`}
                                  ></div>
                                </label>
                              </div>
                            </td>

                            <td className="px-4 py-3 flex items-center gap-3">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (aiEnabled) return aiBlockToast();
                                  handleEdit(t);
                                }}
                                className={`text-xs font-bold transition cursor-pointer ${
                                  aiEnabled
                                    ? "text-slate-400"
                                    : "text-slate-900 hover:text-emerald-700 underline"
                                }`}
                              >
                                Edit
                              </button>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (aiEnabled) return aiBlockToast();
                                  openDeleteConfirm(t._id);
                                }}
                                className={`text-xs font-bold transition cursor-pointer ${
                                  aiEnabled
                                    ? "text-slate-400"
                                    : "text-red-600 hover:text-red-800 underline"
                                }`}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="p-8 text-center">
                        <div className="flex flex-col items-center gap-3 py-6">
                          <p className="text-xs font-semibold text-slate-700">
                            No custom templates yet
                          </p>
                          <p className="max-w-sm text-[11px] text-slate-500">
                            Custom templates let you reply to leads that do not
                            come from the Shopify Partner Directory.
                          </p>
                          <button
                            type="button"
                            onClick={handleCreateNew}
                            className="rounded-full bg-[#111110] px-5 py-2 text-xs font-bold text-white transition hover:bg-black cursor-pointer"
                          >
                            Create your first template
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className="block sm:hidden divide-y divide-slate-200">
              {loading ? (
                <Loader />
              ) : Object.keys(groupedTemplates).length > 0 ? (
                Object.entries(groupedTemplates).map(([srv, group]) => (
                  <div key={srv} className="bg-slate-50">
                    <h3 className="px-4 py-2.5 font-bold text-slate-900 text-xs bg-slate-100 border-b border-slate-200">
                      {srv}
                    </h3>

                    {group.map((t) => (
                      <div
                        key={t._id}
                        className={`p-4 bg-white border-b border-slate-100 ${
                          aiEnabled ? "opacity-70" : "hover:bg-slate-50 transition"
                        }`}
                        onClick={() => aiEnabled && aiBlockToast()}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-bold text-slate-900 text-xs">
                            {t.name}
                          </h4>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (aiEnabled) return aiBlockToast();
                              handleEdit(t);
                            }}
                            className={`text-xs font-bold ${
                              aiEnabled
                                ? "text-slate-400"
                                : "text-slate-900 hover:underline cursor-pointer"
                            }`}
                          >
                            Edit
                          </button>
                        </div>

                        <p className="text-xs text-slate-600 mb-2 font-normal">
                          {aiEnabled ? (
                            <span className="text-emerald-700 font-bold">
                              ⚡ Auto Reply (AI Enabled)
                            </span>
                          ) : (
                            <>
                              {t.content.replace(/<[^>]+>/g, "").slice(0, 100)}
                              ...
                            </>
                          )}
                        </p>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500 font-bold">
                            {t.active ? "Active" : "Inactive"}
                          </span>

                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={t.active}
                              disabled={aiEnabled}
                              onChange={() => {
                                if (aiEnabled) return aiBlockToast();
                                handleToggle(t._id, t.active);
                              }}
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-slate-300 rounded-full peer-checked:bg-emerald-600 transition-colors"></div>
                            <div className="absolute left-[2px] top-[2px] w-4 h-4 bg-white rounded-full shadow-xs transition-transform peer-checked:translate-x-4"></div>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              ) : (
                <div className="p-8 text-center">
                  <div className="flex flex-col items-center gap-3 py-6">
                    <p className="text-xs font-semibold text-slate-700">
                      No custom templates yet
                    </p>
                    <p className="max-w-sm text-[11px] text-slate-500">
                      Custom templates let you reply to leads that do not
                      come from the Shopify Partner Directory.
                    </p>
                    <button
                      type="button"
                      onClick={handleCreateNew}
                      className="rounded-full bg-[#111110] px-5 py-2 text-xs font-bold text-white transition hover:bg-black cursor-pointer"
                    >
                      Create your first template
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        {/* Drawer Side Modal */}
        <div className="fixed inset-0 z-[9999] flex pointer-events-none">
          <div
            className={`flex-1 bg-black/50 backdrop-blur-xs transition-opacity duration-300 ${
              isDrawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0"
            }`}
            onClick={() => setIsDrawerOpen(false)}
          ></div>

          <div
            className={`w-full sm:max-w-lg lg:max-w-xl bg-white shadow-2xl h-full flex flex-col transform transition-transform duration-300 ease-in-out pointer-events-auto ${
              isDrawerOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Drawer Dark Header Bar */}
            <div className="flex justify-between items-center px-6 py-4 bg-[#111110] text-white shrink-0">
              <div>
                <h2 className="text-base font-bold text-white">
                  {editingId ? "Edit Template" : "Create New Template"}
                </h2>
                <p className="text-xs text-slate-300 mt-0.5 font-normal">
                  Customize automated custom template response content
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsDrawerOpen(false)}
                className="text-slate-400 hover:text-white transition p-1 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1.5">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g. Pricing enquiry reply"
                  className="w-full p-3 border border-slate-300 rounded-[8px] text-xs font-semibold text-slate-900 outline-none focus:border-slate-800 transition"
                />
                <p className="mt-1.5 text-[11px] text-slate-500">
                  How you will recognise this template in lists and pickers.
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1.5">
                  Service Request
                </label>
                <input
                  type="text"
                  value={service || "Any (general)"}
                  readOnly
                  className="w-full p-3 border border-slate-200 rounded-[8px] bg-slate-100/80 text-slate-700 text-xs font-semibold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-800 mb-2">
                  Template Response Body
                </label>

                <div className="template-editor border border-slate-300 rounded-[8px] overflow-hidden shadow-2xs">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    className="bg-white"
                  />
                </div>

                <div className="mt-4 border border-slate-200 rounded-[10px] bg-slate-50/80 p-4 shadow-2xs">
                  <h3 className="text-xs font-bold text-slate-900 mb-2.5">
                    Insert Field Variables
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {[
                      /*
                        Greeting first: it is the one most templates want.
                        It resolves to "Hi Chris" when the lead gave a
                        name and "Dear Sir/Madam" when they pasted an
                        email address instead, so an opening line built on
                        it reads correctly either way.
                      */
                      { label: "Greeting", placeholder: "{{Greeting}}" },
                      { label: "First name", placeholder: "{{FirstName}}" },
                      { label: "Full name", placeholder: "{{FullName}}" },
                      {
                        label: "Business email",
                        placeholder: "{{BusinessEmail}}",
                      },
                      { label: "Store name", placeholder: "{{StoreName}}" },
                      { label: "Store URL", placeholder: "{{StoreURL}}" },
                      { label: "Country", placeholder: "{{Country}}" },
                      { label: "Service", placeholder: "{{Service}}" },
                      { label: "Budget", placeholder: "{{Budget}}" },
                      {
                        label: "Problem & Goal",
                        placeholder: "{{ProblemGoal}}",
                      },
                    ].map((field, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => insertField(field.placeholder)}
                        className="px-2.5 py-1 bg-white text-slate-800 text-xs font-bold rounded-[6px] border border-slate-300 hover:bg-slate-100 hover:border-slate-400 transition cursor-pointer shadow-2xs"
                      >
                        {field.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-5 border-t border-slate-200 bg-slate-50 flex justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={() => setIsDrawerOpen(false)}
                className="px-5 py-2.5 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-[8px] hover:bg-slate-100 transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveTemplate}
                className="px-5 py-2.5 text-xs font-bold text-white bg-[#111110] hover:bg-black rounded-[8px] transition cursor-pointer shadow-xs"
              >
                {editingId ? "Update Template" : "Save Template"}
              </button>
            </div>
          </div>
        </div>

        {/* Delete Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-[12px] w-full max-w-sm border border-slate-200 shadow-2xl overflow-hidden">
              <div className="bg-[#111110] text-white px-6 py-4 flex items-center justify-between">
                <h2 className="text-base font-bold text-white">
                  Delete Template?
                </h2>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="text-slate-400 hover:text-white transition cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="p-6">
                <p className="text-slate-600 text-xs font-medium mb-6">
                  Are you sure you want to delete this custom template? This action cannot be undone.
                </p>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-4 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-[8px] hover:bg-slate-100 transition cursor-pointer"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={handleDeleteTemplate}
                    className="px-4 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-[8px] transition cursor-pointer shadow-2xs"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade Modal */}
        {showUpgradeModal && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="relative bg-white rounded-[12px] shadow-2xl border border-slate-200 max-w-md w-full overflow-hidden">
              <div className="flex justify-between items-center px-6 py-4 bg-[#111110] text-white">
                <div className="flex items-center gap-2">
                  <Sparkles className="text-emerald-400" size={20} />
                  <h2 className="text-base font-bold text-white">
                    Unlock AI Templates
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => setShowUpgradeModal(false)}
                  className="text-slate-400 hover:text-white transition cursor-pointer"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6">
                <p className="text-xs text-slate-600 font-normal leading-relaxed">
                  Take your productivity to the next level. Generate high-converting emails in seconds with our <strong>Pro AI engine</strong>.
                </p>

                <div className="mt-5 space-y-2.5">
                  {[
                    "Unlimited AI Generations",
                    "Custom Brand Voice",
                    "Priority Support",
                  ].map((feature) => (
                    <div
                      key={feature}
                      className="flex items-center gap-2.5 text-xs font-semibold text-slate-800"
                    >
                      <CheckCircle2 className="text-emerald-600 shrink-0" size={16} />
                      {feature}
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex flex-col gap-2.5">
                  <button
                    type="button"
                    onClick={() => {
                      setShowUpgradeModal(false);
                      navigate("/pricing");
                    }}
                    className="w-full py-2.5 px-4 bg-[#111110] hover:bg-black text-white font-bold text-xs rounded-[8px] shadow-xs transition cursor-pointer"
                  >
                    Upgrade to Pro
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowUpgradeModal(false)}
                    className="w-full py-2 text-xs font-semibold text-slate-500 hover:text-slate-800 transition cursor-pointer"
                  >
                    Maybe later
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
